<template>
  <div
    class="field-control eds-input"
    :class="[multiple ? multiSelectFieldClass : '', fieldControlClass]"
    :style="fieldControlStyle"
  >
    <div
      v-if="$slots.prepend"
      class="eds-input-addon input-prepend"
      :class="prependInputClass"
      :style="prependInputStyle"
    >
      <span
        class="material-icons input-addon-content input-addon-text"
        :class="prependAddonClass"
        :style="prependAddonStyle"
      >
        <slot name="prepend" />
      </span>
    </div>
    <select
      v-model="selectedItem"
      class="input-element input-dropdown"
      :class="[{ 'input-multiselect': multiple }, dropdownInputClass]"
      :disabled="disabled"
      :style="dropdownInputStyle"
      :size="multiple ? size : undefined"
      :multiple="multiple"
      @change="optionSelected()"
      @click="onClick"
    >
      <option value="" disabled selected v-if="showPlaceHolder">
        {{ placeholder }}
      </option>
      <option
        v-for="(item, index) in options"
        :key="index"
        :value="getOptionValue(item)"
        :class="optionClass"
        :style="optionStyle"
      >
        <slot name="option" :item="item" />
        <span v-if="!$slots.option">{{ getOptionLabel(item) }}</span>
      </option>
    </select>
    <div
      v-if="$slots.append"
      class="eds-input-addon input-append"
      :class="appendInputClass"
      :style="appendInputStyle"
    >
      <span
        class="material-icons input-addon-content input-icon eds-link"
        :class="appendAddonClass"
        :style="appendAddonStyle"
      >
        <slot name="append" />
      </span>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType, Ref, ref, toRefs, watch } from 'vue';

  export default defineComponent({
    name: 'Select',
    inheritAttrs: false,
    emits: ['update:modelValue', 'click'],
    props: {
      /**
       * Disable the entire component.
       * @type {Boolean}
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      label: {
        type: String,
        default: 'name',
      },
      value: {
        type: String,
        default: 'value',
      },
      options: {
        type: Array as PropType<Array<any>>,
        required: false,
        default: () => [],
      },
      multiple: {
        type: Boolean,
        required: false,
        default: false,
      },
      modelValue: {
        type: [String, Number, Array],
        required: false,
        // only props declared above are available in argument, better to move prop as a last one while using props as argument
        default: (props: any) => {
          return props?.multiple ? [] : '';
        },
      },
      size: {
        type: Number,
        required: false,
        default: 4,
      },
      showPlaceHolder: {
        type: Boolean,
        required: false,
        default: true,
      },
      fieldControlClass: {
        type: String,
        required: false,
        default: '',
      },
      fieldControlStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      multiSelectFieldClass: {
        type: String,
        required: false,
        default: '',
      },
      prependInputClass: {
        type: String,
        required: false,
        default: '',
      },
      prependInputStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      prependAddonClass: {
        type: String,
        required: false,
        default: '',
      },
      prependAddonStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      dropdownInputClass: {
        type: String,
        required: false,
        default: '',
      },
      dropdownInputStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      optionClass: {
        type: String,
        required: false,
        default: '',
      },
      optionStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      appendInputClass: {
        type: String,
        required: false,
        default: '',
      },
      appendInputStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      appendAddonClass: {
        type: String,
        required: false,
        default: '',
      },
      appendAddonStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
    },
    setup: (props, { attrs, emit }) => {
      const selectedItem = ref('') as Ref<any>;
      const { modelValue, showPlaceHolder } = toRefs(props);
      selectedItem.value = modelValue.value;
      const placeholder = attrs.placeholder || 'Select...';
      watch(modelValue, (newValue) => {
        selectedItem.value = newValue;
      });
      const onClick = () => {
        emit('click');
      };
      const optionSelected = () => {
        let selectedValues = [] as any;
        selectedValues = selectedItem.value;
        emit('update:modelValue', selectedValues);
      };

      return {
        selectedItem,
        placeholder,
        optionSelected,
        onClick,
      };
    },
    methods: {
      getOptionLabel(option: any): Object {
        return option[this.label];
      },
      getOptionValue(option: any): Object {
         return option[this.value] ? option[this.value] : option['name'];
      },
    },
  });
</script>
